import * as THREE from "three";
import _ from "lodash";
import {
  SelectiveBloomEffect,
  EffectComposer,
  EffectPass,
  GodRaysEffect,
  BrightnessContrastEffect,
  HueSaturationEffect,
  RenderPass,
  SSAOEffect,
  KernelSize,
  OutlineEffect,
  BlendFunction,
} from "postprocessing";

class Renderer {
  constructor(graphicCanvas) {
    if (!Renderer.glRenderer) {
      Renderer.glRenderer = new THREE.WebGLRenderer({
        canvas: graphicCanvas,
        antialias: false,
        alpha: false,
        preserveDrawingBuffer: true,
        // powerPreference: "high-performance",
      });
    }
    this.glRenderer = Renderer.glRenderer;

    // NOTE: In a pc environment, window.devicePixelRatio return value is 2.
    // If we want to use antialias option for WebGLRender, it is to high to keep 60 frame.
    // so give value 1.5 to setPixelRation temporary.
    this.glRenderer.setPixelRatio(window.devicePixelRatio);
    this.glRenderer.setSize(graphicCanvas.clientWidth, graphicCanvas.clientHeight, false);

    this.glRenderer.setClearColor(0xaaaaaa);
    this.glRenderer.shadowMap.enabled = true;
    this.glRenderer.outputEncoding = THREE.sRGBEncoding;

    this.composer = new EffectComposer(this.glRenderer);

    // object attributes need to be seperated into defualt & gui
    this.renderPass = {
      scene: null,
      camera: null,
      object: null, // RenderPass object
    };

    this.ssao = {
      effect: null,
      effectPass: null,
    };

    this.bloom = {
      effect: null,
      effectPass: null,
    };

    this.godrays = {
      effect: null,
      effectPass: null,
      light: null,
      sun: null,
    };

    this.outline = {
      effect: null,
      effectPass: null,
      selection: [],
    };

    this.hueSaturation = {
      effect: null,
      effectPass: null,
    };

    this.brightnessContrast = {
      effect: null,
      effectPass: null,
    };
  }

  // private, internal
  _updateRenderPass(scene, camera) {
    if (this.renderPass.scene === scene && this.renderPass.camera === camera) {
      return;
    }
    this.composer.removePass(this.renderPass.object);
    this.renderPass.scene = scene;
    this.renderPass.camera = camera;
    this.renderPass.object = new RenderPass(scene, camera);
    this.composer.addPass(this.renderPass.object, 0);
  }

  // public
  createSSAO(scene, camera) {
    this.ssao.effect = new SSAOEffect();
    // userData.postProcessing will be setted as undefined when scene is loaded from file. also in published project
    // need to check 2 or more post processing can apply to published project
    if (scene.userData.postProcessing.ssao === undefined) {
      scene.userData.postProcessing.ssao = {};
    }

    if (Object.keys(scene.userData.postProcessing.ssao).length === 0) {
      // Setup the default values if there is no configuration
      scene.userData.postProcessing.ssao = {
        bias: 0.01,
        intensity: 1,
        fade: 0.01,
        radius: 0.2,
        rings: 5,
        samples: 15,
        proximityThreshold: 0.1,
        color: "#252525",
      };
    }

    const {color, bias, intensity, radius, rings, samples, proximityThreshold} = scene.userData.postProcessing.ssao;

    this.ssao.effect.color = color;
    this.ssao.effect.ssaoMaterial.bias = bias;
    this.ssao.effect.ssaoMaterial.intensity = intensity;
    this.ssao.effect.ssaoMaterial.radius = radius;
    this.ssao.effect.ssaoMaterial.rings = rings;
    this.ssao.effect.ssaoMaterial.samples = samples;
    this.ssao.effect.ssaoMaterial.proximityThreshold = proximityThreshold;

    this._updateRenderPass(scene, camera);

    this.ssao.effectPass = new EffectPass(camera, this.ssao.effect);
    this.ssao.effectPass.name = "SSAO";
    this.composer.addPass(this.ssao.effectPass);
  }

  updateSSAO(configuration) {
    const {color, bias, intensity, fade} = configuration;

    if (bias !== undefined) {
      this.ssao.effect.ssaoMaterial.bias = bias;
      this.renderPass.scene.userData.postProcessing.ssao.bias = bias;
      return;
    }

    if (intensity !== undefined) {
      this.ssao.effect.ssaoMaterial.intensity = intensity;
      this.renderPass.scene.userData.postProcessing.ssao.intensity = intensity;
      return;
    }

    if (fade !== undefined) {
      this.ssao.effect.ssaoMaterial.fade = fade;
      this.renderPass.scene.userData.postProcessing.ssao.fade = fade;
      return;
    }

    if (color !== undefined) {
      this.ssao.effect.color.setHex("0x" + color.slice(1));
      this.renderPass.scene.userData.postProcessing.ssao.color = color;
      return;
    }
  }

  removeSSAO(resetProperty = false) {
    if (!this.ssao.effect) return;
    if (resetProperty) {
      this.renderPass.scene.userData.postProcessing.ssao = {};
    }
    this.composer.removePass(this.ssao.effectPass);

    this.ssao.effect.dispose();
    this.ssao.effectPass.dispose();
    delete this.ssao.effect;
    delete this.ssao.effectPass;
  }

  createBloom(scene, camera) {
    this.bloom.effect = new SelectiveBloomEffect(scene, camera, {
      blendFunction: BlendFunction.ADD,
      mipmapBlur: true,
      luminanceThreshold: 0.5,
      luminanceSmoothing: 0.3,
      intensity: 3.0,
    });
    if (scene.userData.postProcessing.bloom === undefined) {
      scene.userData.postProcessing.bloom = {};
    }

    if (Object.keys(scene.userData.postProcessing.bloom).length === 0) {
      scene.userData.postProcessing.bloom = {
        opacity: 1,
        blendFunction: "ADD",
        inverted: false,
        luminance: false,
        threshold: 0.5,
        smoothing: 0.3,
        intensity: 0.5,
        kernelSize: KernelSize.HUGE,
        radius: 1,
      };
    }

    let {intensity, kernelSize, radius, opacity, blendFunction, luminance, inverted, threshold, smoothing} =
      scene.userData.postProcessing.bloom;

    if (kernelSize === undefined) kernelSize = KernelSize.HUGE;

    this.bloom.effect.intensity = intensity;
    this.bloom.effect.kernelSize = kernelSize;
    this.bloom.effect.mipmapBlurPass.radius = radius || 1;
    this.bloom.effect.blendMode.opacity.value = opacity || 1;
    this.bloom.effect.luminanceMaterial.threshold = threshold || 0.5;
    this.bloom.effect.luminanceMaterial.smoothing = smoothing || 0.3;
    if (luminance === undefined) {
      this.bloom.effect.luminancePass.enabled = false;
    } else {
      this.bloom.effect.luminancePass.enabled = luminance;
    }
    if (inverted === undefined) {
      this.bloom.effect.inverted = false;
    } else {
      this.bloom.effect.inverted = inverted;
    }

    this._updateRenderPass(scene, camera);

    this.bloom.effectPass = new EffectPass(camera, this.bloom.effect);
    this.bloom.effectPass.name = "BLOOM";
    this.bloom.effect.blendMode.setBlendFunction(BlendFunction[blendFunction || "ADD"]);
    this.composer.addPass(this.bloom.effectPass);
  }

  updateBloom(configuration) {
    const {intensity, kernelSize, radius, opacity, blendFunction, luminance, inverted, threshold, smoothing} = configuration;
    if (intensity !== undefined) {
      this.bloom.effect.intensity = intensity;
      this.renderPass.scene.userData.postProcessing.bloom.intensity = intensity;
      return;
    }

    if (radius !== undefined) {
      this.bloom.effect.mipmapBlurPass.radius = radius;
      this.renderPass.scene.userData.postProcessing.bloom.radius = radius;
      return;
    }

    if (opacity !== undefined) {
      this.bloom.effect.blendMode.opacity.value = opacity;
      this.renderPass.scene.userData.postProcessing.bloom.opacity = opacity;
      return;
    }

    if (blendFunction !== undefined) {
      this.bloom.effect.blendMode.setBlendFunction(BlendFunction[blendFunction]);
      this.renderPass.scene.userData.postProcessing.bloom.blendFunction = blendFunction;
      return;
    }

    if (luminance !== undefined) {
      this.bloom.effect.luminancePass.enabled = luminance;
      this.renderPass.scene.userData.postProcessing.bloom.luminance = luminance;
      return;
    }

    if (inverted !== undefined) {
      this.bloom.effect.inverted = inverted;
      this.renderPass.scene.userData.postProcessing.bloom.inverted = inverted;
      return;
    }

    if (threshold !== undefined) {
      this.bloom.effect.luminanceMaterial.threshold = threshold;
      this.renderPass.scene.userData.postProcessing.bloom.threshold = threshold;
      return;
    }

    if (smoothing !== undefined) {
      this.bloom.effect.luminanceMaterial.smoothing = smoothing;
      this.renderPass.scene.userData.postProcessing.bloom.smoothing = smoothing;
      return;
    }
  }

  removeBloom(resetProperty = false) {
    if (!this.bloom.effect) return;
    if (resetProperty) {
      this.renderPass.scene.userData.postProcessing.bloom = {};
    }

    this.composer.removePass(this.bloom.effectPass);

    this.bloom.effect.dispose();
    this.bloom.effectPass.dispose();
    delete this.bloom.effect;
    delete this.bloom.effectPass;
  }

  createGodrays(scene, camera) {
    // Lights
    if (!this.godrays.light) {
      const mainLight = new THREE.PointLight(0xffe3b1);
      mainLight.position.set(-0.5, 15, -0.25);
      mainLight.castShadow = true;
      mainLight.shadow.bias = 0.0000125;
      mainLight.title = "PointLight" + "(" + mainLight.uuid.substring(0, 3) + ")";
      mainLight.shadow.mapSize.width = 512;
      mainLight.shadow.mapSize.height = 512;

      this.godrays.light = mainLight;
      scene.add(mainLight);

      // Sun
      const sunMaterial = new THREE.MeshBasicMaterial({
        color: 0xffddaa,
        transparent: true,
        fog: false,
      });

      const sunGeometry = new THREE.SphereBufferGeometry(0.75, 32, 32);
      const sun = new THREE.Mesh(sunGeometry, sunMaterial);
      sun.name = "sun";
      sun.title = "sun" + "(" + sun.uuid.substring(0, 3) + ")";
      sun.frustumCulled = false;
      sun.matrixAutoUpdate = false;

      const group = new THREE.Group();
      group.position.copy(this.godrays.light.position);
      group.add(sun);

      this.godrays.sun = sun;
    }
    // Godray Effect
    this.godrays.effect = new GodRaysEffect(camera, this.godrays.sun);

    // TODO: Default Option setting
    // this.godrays.effect = new GodRaysEffect(camera, sun, {
    //   height: 480,
    //   kernelSize: KernelSize.VERY_LARGE,
    //   density: 0.96,
    //   decay: 0.92,
    //   weight: 0.3,
    //   exposure: 0.54,
    //   samples: 2,
    //   clampMax: 1.0,
    // });

    if (scene.userData.postProcessing.godrays === undefined) {
      scene.userData.postProcessing.godrays = {};
    }

    if (Object.keys(scene.userData.postProcessing.godrays).length === 0) {
      scene.userData.postProcessing.godrays = {
        weight: 0.3,
        density: 0.95,
        samples: 2,
        color: "#ffe3b1",
      };
    }

    const {weight, density, samples} = scene.userData.postProcessing.godrays;

    this.godrays.effect.godRaysMaterial.weight = weight;
    this.godrays.effect.godRaysMaterial.density = density;
    this.godrays.effect.godRaysMaterial.samples = samples;

    this._updateRenderPass(scene, camera);

    this.godrays.effectPass = new EffectPass(camera, this.godrays.effect);
    this.godrays.effectPass.name = "GODRAYS";
    this.composer.addPass(this.godrays.effectPass);
  }

  updateGodrays(configuration) {
    const {color, weight, density} = configuration;

    if (color !== undefined) {
      this.godrays.sun.material.color.setHex("0x" + color.slice(1));
      this.godrays.light.color.setHex("0x" + color.slice(1));
      this.renderPass.scene.userData.postProcessing.godrays.color = color;
    }

    if (weight !== undefined) {
      this.godrays.effect.godRaysMaterial.weight = weight;
      this.renderPass.scene.userData.postProcessing.godrays.weight = weight;
      return;
    }

    if (density !== undefined) {
      this.godrays.effect.godRaysMaterial.density = density;
      this.renderPass.scene.userData.postProcessing.godrays.density = density;
      return;
    }
  }

  removeGodrays(resetProperty) {
    if (!this.godrays.effect) return;
    if (resetProperty) {
      this.renderPass.scene.userData.postProcessing.godrays = {};
      // remove pointLight
      this.renderPass.scene.remove(this.godrays.light);
      delete this.godrays.light;
      delete this.godrays.sun;
    }

    this.composer.removePass(this.godrays.effectPass);

    this.godrays.effect.dispose();
    this.godrays.effectPass.dispose();
    delete this.godrays.effect;
    delete this.godrays.effectPass;
  }

  createOutline(scene, camera, target) {
    this.outline.effect = new OutlineEffect(scene, camera, {
      blendFunction: BlendFunction.SCREEN,
      edgeStrength: 4.5,
      pulseSpeed: 0.35,
      visibleEdgeColor: 0xffff00,
      hiddenEdgeColor: 0x22090a,
      blur: true,
      kernelSize: KernelSize.MEDIUM,
      xRay: true,
      // patternTexture: null,
      // patternScale: 1.0,
      // multisampling: 0,
      // resolutionScale: 0.5,
      // resolutionX: Resolution.AUTO_SIZE,
      // resolutionY: Resolution.AUTO_SIZE,
    });

    target.traverse(child => this.outline.selection.push(child));

    this.outline.effectPass = new EffectPass(camera, this.outline.effect);
    this.outline.effectPass.name = "OUTLINE";
    this.composer.addPass(this.outline.effectPass);
  }

  activateOutline(active) {
    if (active) this.outline.effect.selection.set(this.outline.selection);
    else this.outline.effect.selection.set([]);
  }

  removeOutline() {
    if (!this.outline.effect) return;

    this.composer.removePass(this.outline.effectPass);

    this.outline.effect.dispose();
    this.outline.effectPass.dispose();
    delete this.outline.effect;
    delete this.outline.effectPass;
    this.outline.selection = [];
  }

  createHueSaturation(scene, camera) {
    if (scene.userData.postProcessing.hueSaturation === undefined) {
      scene.userData.postProcessing.hueSaturation = {};
    }

    if (Object.keys(scene.userData.postProcessing.hueSaturation).length === 0) {
      scene.userData.postProcessing.hueSaturation = {
        blendFunction: "ADD",
        hue: 0,
        saturation: 0,
        opacity: 0,
      };
    }

    let {blendFunction, hue, saturation, opacity} = scene.userData.postProcessing.hueSaturation;
    this.hueSaturation.effect = new HueSaturationEffect({
      blendFunction: BlendFunction[blendFunction],
      hue: hue * (Math.PI / 180),
      saturation: saturation,
    });
    this.hueSaturation.effectPass = new EffectPass(camera, this.hueSaturation.effect);
    this.hueSaturation.effect.blendMode.opacity.value = opacity;
    this._updateRenderPass(scene, camera);

    this.hueSaturation.effectPass.name = "HUESATURATION";
    this.composer.addPass(this.hueSaturation.effectPass);
  }

  updateHueSaturation(configuration) {
    const {blendFunction, hue, saturation, opacity} = configuration;

    if (blendFunction !== undefined) {
      this.hueSaturation.effect.blendMode.setBlendFunction(BlendFunction[blendFunction]);
      this.renderPass.scene.userData.postProcessing.hueSaturation.blendFunction = blendFunction;
      return;
    }

    if (hue !== undefined) {
      this.hueSaturation.effect.hue = hue * (Math.PI / 180);
      this.renderPass.scene.userData.postProcessing.hueSaturation.hue = hue;
      return;
    }

    if (saturation !== undefined) {
      this.hueSaturation.effect.saturation = saturation;
      this.renderPass.scene.userData.postProcessing.hueSaturation.saturation = saturation;
      return;
    }

    if (opacity !== undefined) {
      this.hueSaturation.effect.blendMode.opacity.value = opacity;
      this.renderPass.scene.userData.postProcessing.hueSaturation.opacity = opacity;
      return;
    }
  }

  removeHueSaturation(resetProperty = false) {
    if (!this.hueSaturation.effect) return;
    if (resetProperty) {
      this.renderPass.scene.userData.postProcessing.hueSaturation = {};
    }

    this.composer.removePass(this.hueSaturation.effectPass);

    this.hueSaturation.effect.dispose();
    this.hueSaturation.effectPass.dispose();
    delete this.hueSaturation.effect;
    delete this.hueSaturation.effectPass;
  }

  createBrightnessContrast(scene, camera) {
    if (scene.userData.postProcessing.brightnessContrast === undefined) {
      scene.userData.postProcessing.brightnessContrast = {};
    }

    if (Object.keys(scene.userData.postProcessing.brightnessContrast).length === 0) {
      scene.userData.postProcessing.brightnessContrast = {
        blendFunction: "ADD",
        brightness: 0.5,
        contrast: 0.5,
        opacity: 0,
      };
    }

    let {blendFunction, brightness, contrast, opacity} = scene.userData.postProcessing.brightnessContrast;
    this.brightnessContrast.effect = new BrightnessContrastEffect({
      blendFunction: BlendFunction[blendFunction],
    });

    this.brightnessContrast.effect.brightness = brightness;
    this.brightnessContrast.effect.contrast = contrast;
    this.brightnessContrast.effect.blendMode.opacity.value = opacity;
    this.brightnessContrast.effectPass = new EffectPass(camera, this.brightnessContrast.effect);
    this._updateRenderPass(scene, camera);

    this.brightnessContrast.effectPass.name = "BRIGHTNESSCONTRAST";
    this.composer.addPass(this.brightnessContrast.effectPass);
  }

  updateBrightnessContrast(configuration) {
    const {blendFunction, brightness, contrast, opacity} = configuration;

    if (blendFunction !== undefined) {
      this.brightnessContrast.effect.blendMode.setBlendFunction(BlendFunction[blendFunction]);
      this.renderPass.scene.userData.postProcessing.brightnessContrast.blendFunction = blendFunction;
      return;
    }

    if (brightness !== undefined) {
      this.brightnessContrast.effect.brightness = brightness;
      this.renderPass.scene.userData.postProcessing.brightnessContrast.brightness = brightness;
      return;
    }

    if (contrast !== undefined) {
      this.brightnessContrast.effect.contrast = contrast;
      this.renderPass.scene.userData.postProcessing.brightnessContrast.contrast = contrast;
      return;
    }

    if (opacity !== undefined) {
      this.brightnessContrast.effect.blendMode.opacity.value = opacity;
      this.renderPass.scene.userData.postProcessing.brightnessContrast.opacity = opacity;
      return;
    }
  }

  removeBrightnessContrast(resetProperty = false) {
    if (!this.brightnessContrast.effect) return;
    if (resetProperty) {
      this.renderPass.scene.userData.postProcessing.brightnessContrast = {};
    }

    this.composer.removePass(this.brightnessContrast.effectPass);

    this.brightnessContrast.effect.dispose();
    this.brightnessContrast.effectPass.dispose();
    delete this.brightnessContrast.effect;
    delete this.brightnessContrast.effectPass;
  }

  updatePostProcess(scene, camera) {
    this._updateRenderPass(scene, camera);
    // If there is no postprocessing in scene's userData update Render pass and return
    if (scene.userData.postProcessing === undefined) return;
    if (this.ssao.effect || !_.isEmpty(scene.userData.postProcessing.ssao)) {
      this.removeSSAO();
      this.createSSAO(scene, camera);
    }
    if (this.bloom.effect || !_.isEmpty(scene.userData.postProcessing.bloom)) {
      this.removeBloom();
      this.createBloom(scene, camera);
    }
    if (this.godrays.effect || !_.isEmpty(scene.userData.postProcessing.godrays)) {
      this.removeGodrays();
      this.createGodrays(scene, camera);
    }
    if (this.hueSaturation.effect || !_.isEmpty(scene.userData.postProcessing.hueSaturation)) {
      this.removeHueSaturation();
      this.createHueSaturation(scene, camera);
    }
    if (this.brightnessContrast.effect || !_.isEmpty(scene.userData.postProcessing.brightnessContrast)) {
      this.removeBrightnessContrast();
      this.createBrightnessContrast(scene, camera);
    }
  }

  render(scene, camera, autoclear = true) {
    if (!autoclear) {
      this.glRenderer.clearDepth();
      this.glRenderer.render(scene, camera);
    } else {
      if (!this.renderPass.object) {
        this._updateRenderPass(scene, camera);
      }
      this.composer.render();
    }
  }
}

export default Renderer;
